import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

const NAV_LINKS = [
  { to: '/', label: '首页', icon: 'ri-home-5-line' },
  { to: '/category/movie', label: '电影', icon: 'ri-film-line' },
  { to: '/category/tv', label: '电视剧', icon: 'ri-tv-2-line' },
  { to: '/category/variety', label: '综艺', icon: 'ri-mic-line' },
  { to: '/about', label: '关于我们', icon: 'ri-information-line' },
];

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
    setMenuOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled ? 'border-background-200 bg-background-50/95 backdrop-blur-md' : 'border-transparent bg-background-50'
      }`}
    >
      <div className="mx-auto flex h-16 items-center gap-4 px-4 md:h-[68px] md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-play-fill text-xl" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-base font-extrabold tracking-tight text-foreground-950">
              91视频在线
            </span>
            <span className="font-label mt-0.5 hidden text-[10px] font-medium tracking-wide text-foreground-500 sm:block">
              高清在线视频播放平台
            </span>
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 md:flex">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === '/'}
              className={({ isActive }) =>
                `font-label rounded-md px-3.5 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <form onSubmit={submitSearch} className="ml-auto hidden items-center md:flex">
          <div className="flex items-center gap-2 rounded-md border border-background-300/80 bg-background-100 px-3 py-2 transition-colors focus-within:border-primary-300 focus-within:bg-background-50">
            <i className="ri-search-line text-base text-foreground-400" />
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影片、电视剧、综艺"
              className="w-44 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none lg:w-56"
              aria-label="搜索影视内容"
            />
            <button
              type="submit"
              className="font-label cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </div>
        </form>

        <div className="ml-auto flex items-center gap-2 md:ml-0">
          <Link
            to="/search"
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300/80 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 md:hidden"
            aria-label="搜索"
          >
            <i className="ri-search-line text-lg" />
          </Link>
          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300/80 text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600 md:hidden"
            aria-label="打开菜单"
            aria-expanded={menuOpen}
          >
            <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
          </button>
        </div>
      </div>

      {menuOpen ? (
        <div className="animate-fade-in border-t border-background-200 bg-background-50 px-4 pb-4 pt-3 md:hidden">
          <form onSubmit={submitSearch} className="mb-3 flex items-center gap-2 rounded-md border border-background-300/80 bg-background-100 px-3 py-2">
            <i className="ri-search-line text-base text-foreground-400" />
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影片、电视剧、综艺"
              className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              aria-label="搜索影视内容"
            />
          </form>
          <nav className="grid grid-cols-2 gap-2">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === '/'}
                className={({ isActive }) =>
                  `font-label flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive ? 'bg-primary-50 text-primary-700' : 'bg-background-100 text-foreground-700'
                  }`
                }
              >
                <i className={`${link.icon} text-lg`} />
                {link.label}
              </NavLink>
            ))}
          </nav>
        </div>
      ) : null}
    </header>
  );
}